/**
 * TUVI - Salidas de Bodega Administrativa
 * Registro manual de salidas con búsqueda de proyectos
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import './SalidasBodega.css';

const SalidasBodega = () => {
  const { success, error, warning } = useNotification();
  const [salidas, setSalidas] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [inventario, setInventario] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [busquedaProyecto, setBusquedaProyecto] = useState('');
  const [proyectoSeleccionado, setProyectoSeleccionado] = useState(null);
  const [mostrarListaProyectos, setMostrarListaProyectos] = useState(false);
  const [busquedaArticulo, setBusquedaArticulo] = useState('');
  const [articuloSeleccionado, setArticuloSeleccionado] = useState(null);
  const [mostrarListaArticulos, setMostrarListaArticulos] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [modoEdicion, setModoEdicion] = useState(false);
  const [salidaEditando, setSalidaEditando] = useState(null);
  const [mostrarConfirmDelete, setMostrarConfirmDelete] = useState(false);
  const [salidaEliminar, setSalidaEliminar] = useState(null);
  // Si está activo, la salida se registra con precio $0 (no suma al presupuesto del proyecto).
  // Útil cuando el material ya fue pagado en otra factura y no debe volver a cargarse.
  const [noCargarProyecto, setNoCargarProyecto] = useState(false);
  // Capítulos del proyecto elegido: la salida se carga a uno de ellos y suma en
  // lo gastado de ese capítulo.
  const [capitulos, setCapitulos] = useState([]);
  const [capituloId, setCapituloId] = useState('');
  const [cargandoCapitulos, setCargandoCapitulos] = useState(false);

  const [formData, setFormData] = useState({
    articulo_nombre: '',
    unidad_medida: 'unidad',
    cantidad: '',
    precio_unitario: '',
    fecha_movimiento: new Date().toISOString().split('T')[0],
    observaciones: ''
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);

      // Cargar proyectos
      const gqlProys = await client.query(`{ proyectos { id proyecto_nombre nombre_cliente apartamento } }`);
      setProyectos(gqlProys.proyectos || []);

      // Cargar historial de salidas
      const gqlSalidas = await client.query(`{ bodegaMovimientos(tipo: "salida") { id articulo_nombre unidad_medida tipo_movimiento cantidad precio_unitario valor_total proveedor_nombre proyecto_id proyecto_nombre capitulo_id capitulo_nombre numero_factura fecha_movimiento observaciones usuario_registro archivos { id nombre_original ruta } } }`);
      const respSalidas = { json: async () => ({ success: true, movimientos: gqlSalidas.bodegaMovimientos }) };
      const dataSalidas = await respSalidas.json();
      if (dataSalidas.success) {
        setSalidas(dataSalidas.movimientos || []);
      }

      // Cargar inventario real (entradas - salidas) con precio promedio
      const gqlInv = await client.query(`{ bodegaInventarioResumen { articulo_nombre unidad_medida stock_actual precio_promedio } }`);
      const articulosConStock = (gqlInv.bodegaInventarioResumen || []).filter(
        item => parseFloat(item.stock_actual) > 0
      );
      setInventario(articulosConStock);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Trae los capítulos del proyecto. `preseleccion` es el capítulo que ya tenía
  // la salida cuando se abre para editar.
  const cargarCapitulos = async (proyectoId, preseleccion = '') => {
    setCapitulos([]);
    setCapituloId('');
    if (!proyectoId) return;
    setCargandoCapitulos(true);
    try {
      const r = await client.query('query($id: Int!) { proyecto(id: $id) { capitulos { id numero capitulo_nombre } } }', { id: proyectoId });
      const lista = r.proyecto?.capitulos || [];
      setCapitulos(lista);
      if (preseleccion && lista.some(c => c.id === Number(preseleccion))) setCapituloId(String(preseleccion));
    } catch (err) {
      console.error('Error cargando capítulos:', err);
      error('No se pudieron cargar los capítulos del proyecto');
    } finally {
      setCargandoCapitulos(false);
    }
  };

  const handleSeleccionarProyecto = (proyecto) => {
    setProyectoSeleccionado(proyecto);
    setBusquedaProyecto(`${proyecto.proyecto_nombre} - ${proyecto.nombre_cliente}`);
    setMostrarListaProyectos(false);
    cargarCapitulos(proyecto.id);
  };

  // Seleccionar artículo del inventario
  const handleSeleccionarArticulo = (articulo) => {
    setArticuloSeleccionado(articulo);
    setBusquedaArticulo(articulo.articulo_nombre);
    setMostrarListaArticulos(false);
    // Auto-llenar datos del artículo
    setFormData(prev => ({
      ...prev,
      articulo_nombre: articulo.articulo_nombre,
      unidad_medida: articulo.unidad_medida || 'unidad',
      precio_unitario: articulo.precio_promedio || ''
    }));
  };

  // Normalizar texto para búsqueda (quitar tildes, minúsculas, espacios extra)
  const normalizar = (texto) => {
    if (!texto) return '';
    return texto
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Quitar tildes
      .replace(/[^a-z0-9\s]/g, '') // Solo letras, números y espacios
      .trim();
  };

  // Filtrar proyectos para búsqueda
  const proyectosFiltrados = React.useMemo(() => {
    // Si no hay proyectos, retornar vacío
    if (!proyectos || proyectos.length === 0) {
      return [];
    }

    // Si hay un proyecto seleccionado, mostrar todos (por si quiere cambiar)
    if (proyectoSeleccionado) {
      return proyectos;
    }

    // Si no hay texto de búsqueda, mostrar todos
    if (!busquedaProyecto || busquedaProyecto.trim().length === 0) {
      return proyectos;
    }

    const busqueda = normalizar(busquedaProyecto);

    // Filtrar proyectos - buscar en todos los campos relevantes
    return proyectos.filter(proy => {
      const nombre = normalizar(proy.proyecto_nombre);
      const cliente = normalizar(proy.nombre_cliente);
      const apto = normalizar(proy.apartamento);

      // Buscar en cada campo individualmente
      return nombre.includes(busqueda) ||
             cliente.includes(busqueda) ||
             apto.includes(busqueda);
    });
  }, [proyectos, busquedaProyecto, proyectoSeleccionado]);

  // Filtrar artículos del inventario para búsqueda
  const articulosFiltrados = React.useMemo(() => {
    if (!inventario || inventario.length === 0) {
      return [];
    }

    if (articuloSeleccionado) {
      return inventario;
    }

    if (!busquedaArticulo || busquedaArticulo.trim().length === 0) {
      return inventario;
    }

    const busqueda = normalizar(busquedaArticulo);

    return inventario.filter(art => {
      const nombre = normalizar(art.articulo_nombre);
      return nombre.includes(busqueda);
    });
  }, [inventario, busquedaArticulo, articuloSeleccionado]);

  // Filtrar salidas según búsqueda
  const salidasFiltradas = useMemo(() => {
    if (!searchTerm.trim()) return salidas;
    const termino = searchTerm.toLowerCase();
    return salidas.filter(salida =>
      (salida.articulo_nombre || '').toLowerCase().includes(termino) ||
      (salida.numero_factura || '').toLowerCase().includes(termino) ||
      (salida.proyecto_nombre || '').toLowerCase().includes(termino) ||
      (salida.proveedor_nombre || '').toLowerCase().includes(termino) ||
      (salida.observaciones || '').toLowerCase().includes(termino)
    );
  }, [salidas, searchTerm]);

  // Abrir modal para editar
  const abrirModalEditar = (salida) => {
    setModoEdicion(true);
    setSalidaEditando(salida);
    setFormData({
      articulo_nombre: salida.articulo_nombre,
      unidad_medida: salida.unidad_medida || 'unidad',
      cantidad: salida.cantidad,
      precio_unitario: salida.precio_unitario,
      fecha_movimiento: salida.fecha_movimiento,
      observaciones: salida.observaciones || ''
    });
    setBusquedaArticulo(salida.articulo_nombre);
    setArticuloSeleccionado({ articulo_nombre: salida.articulo_nombre, stock_actual: 9999, unidad_medida: salida.unidad_medida || 'unidad', precio_promedio: salida.precio_unitario || 0 });
    // Si la salida se guardó en $0, refleja el modo "no cargar al proyecto"
    setNoCargarProyecto(parseFloat(salida.precio_unitario) === 0);
    if (salida.proyecto_nombre) {
      setBusquedaProyecto(salida.proyecto_nombre);
      setProyectoSeleccionado({
        id: salida.proyecto_id,
        proyecto_nombre: salida.proyecto_nombre,
        nombre_cliente: ''
      });
      cargarCapitulos(salida.proyecto_id, salida.capitulo_id || '');
    }
    setMostrarForm(true);
  };

  // Resetear formulario
  const resetForm = () => {
    setFormData({
      articulo_nombre: '',
      unidad_medida: 'unidad',
      cantidad: '',
      precio_unitario: '',
      fecha_movimiento: new Date().toISOString().split('T')[0],
      observaciones: ''
    });
    setProyectoSeleccionado(null);
    setBusquedaProyecto('');
    setCapitulos([]);
    setCapituloId('');
    setArticuloSeleccionado(null);
    setBusquedaArticulo('');
    setModoEdicion(false);
    setSalidaEditando(null);
    setNoCargarProyecto(false);
  };

  // Confirmar eliminación
  const confirmarEliminar = (salida) => {
    setSalidaEliminar(salida);
    setMostrarConfirmDelete(true);
  };

  // Eliminar salida
  const handleEliminar = async () => {
    if (!salidaEliminar) return;

    try {
      const gqlR = await client.mutate(`mutation($id: Int!) { eliminarMovimientoBodega(id: $id) { success message } }`, { id: salidaEliminar.id });

      if (gqlR.eliminarMovimientoBodega.success) {
        success('Salida eliminada exitosamente');
        setMostrarConfirmDelete(false);
        setSalidaEliminar(null);
        cargarDatos();
      } else {
        error('No se pudo eliminar la salida');
      }
    } catch (err) {
      console.error('Error:', err);
      error('Error al eliminar la salida');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!articuloSeleccionado || !formData.cantidad || !proyectoSeleccionado) {
      warning('Por favor complete los campos obligatorios (Artículo, Cantidad, Proyecto)');
      return;
    }
    if (cargandoCapitulos) {
      warning('Espera a que carguen los capítulos del proyecto');
      return;
    }
    // Si el proyecto tiene capítulos, la salida debe ir a uno: así suma en lo
    // gastado de ese capítulo. Los proyectos que aún no tienen capítulos
    // (vendidos o contratados sin Ficha FFE) se registran sin capítulo.
    if (capitulos.length > 0 && !capituloId) {
      warning('Selecciona el capítulo del proyecto al que se carga la salida');
      return;
    }

    // Validar stock solo en modo nuevo (no en edición)
    if (!modoEdicion) {
      const cantidadSolicitada = parseFloat(formData.cantidad);
      const stockDisponible = parseFloat(articuloSeleccionado.stock_actual);

      if (cantidadSolicitada > stockDisponible) {
        error(`La cantidad solicitada (${cantidadSolicitada}) excede el stock disponible (${stockDisponible} ${articuloSeleccionado.unidad_medida})`);
        return;
      }
    }

    try {
      const proyectoNombre = proyectoSeleccionado.nombre_cliente
        ? `${proyectoSeleccionado.proyecto_nombre} - ${proyectoSeleccionado.nombre_cliente}`
        : proyectoSeleccionado.proyecto_nombre;

      const movInput = {
        articulo_nombre: formData.articulo_nombre, unidad_medida: formData.unidad_medida || 'unidad',
        tipo_movimiento: 'salida', cantidad: parseFloat(formData.cantidad) || 0,
        precio_unitario: noCargarProyecto ? 0 : (parseFloat(formData.precio_unitario) || 0),
        proyecto_id: proyectoSeleccionado.id, proyecto_nombre: proyectoNombre,
        capitulo_id: capituloId ? Number(capituloId) : null,
        fecha_movimiento: formData.fecha_movimiento, observaciones: formData.observaciones || null,
      };

      // Al editar se actualiza la salida existente. Antes se llamaba siempre a
      // "crear" y cada edición dejaba una salida nueva repetida.
      const gqlR = (modoEdicion && salidaEditando?.id)
        ? await client.mutate(`mutation($id: Int!, $input: MovimientoBodegaInput!) { editarMovimientoBodega(id: $id, input: $input) { success message } }`, { id: salidaEditando.id, input: movInput })
        : await client.mutate(`mutation($input: MovimientoBodegaInput!) { crearMovimientoBodega(input: $input) { success message id } }`, { input: movInput });
      const resultado = gqlR.editarMovimientoBodega || gqlR.crearMovimientoBodega;

      if (resultado.success) {
        success(modoEdicion ? 'Salida actualizada exitosamente' : 'Salida registrada exitosamente');
        setMostrarForm(false);
        resetForm();
        cargarDatos();
      } else {
        error('No se pudo procesar la salida');
      }
    } catch (err) {
      console.error('Error:', err);
      error('Error al procesar la salida');
    }
  };

  const formatMoney = (valor) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor || 0);
  };

  const formatDate = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="salidas-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Cargando salidas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="salidas-container">
      {/* Header */}
      <div className="salidas-header">
        <div className="header-left">
          <h2>📤 Salidas de Bodega</h2>
          <p className="subtitle">Registro manual de salidas con relación a proyectos</p>
        </div>
        <div className="header-actions">
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Buscar por artículo, proyecto, factura..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm('')}>×</button>
            )}
          </div>
          <button
            className="btn-nueva-salida"
            onClick={() => {
              resetForm();
              setMostrarForm(true);
            }}
          >
            ➕ Nueva Salida
          </button>
        </div>
      </div>

      {/* Formulario Modal */}
      {mostrarForm && (
        <div className="modal-overlay" onClick={() => { setMostrarForm(false); resetForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modoEdicion ? '✏️ Editar Salida' : '📤 Registrar Salida'}</h2>
              <button className="modal-close" onClick={() => { setMostrarForm(false); resetForm(); }}>
                ×
              </button>
            </div>

            <form className="salida-form" onSubmit={handleSubmit}>
              {/* Búsqueda de Artículo del Inventario */}
              <div className="form-group">
                <label>Artículo del Inventario *</label>
                <div className="busqueda-proveedor">
                  <input
                    type="text"
                    value={busquedaArticulo}
                    onChange={(e) => {
                      setBusquedaArticulo(e.target.value);
                      setMostrarListaArticulos(true);
                      setArticuloSeleccionado(null);
                      setFormData(prev => ({
                        ...prev,
                        articulo_nombre: '',
                        unidad_medida: 'unidad',
                        precio_unitario: ''
                      }));
                    }}
                    onFocus={() => setMostrarListaArticulos(true)}
                    placeholder="Buscar artículo por nombre..."
                    required
                  />
                  {mostrarListaArticulos && articulosFiltrados.length > 0 && (
                    <div className="lista-proveedores">
                      {articulosFiltrados.slice(0, 8).map((art, index) => (
                        <div
                          key={`${art.articulo_nombre}-${index}`}
                          className="proveedor-item"
                          onClick={() => handleSeleccionarArticulo(art)}
                        >
                          <strong>{art.articulo_nombre}</strong>
                          <span className="nit">
                            Stock: {art.stock_actual} {art.unidad_medida} | Precio: {formatMoney(art.precio_promedio)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {mostrarListaArticulos && articulosFiltrados.length === 0 && busquedaArticulo && (
                    <div className="lista-proveedores">
                      <div className="proveedor-item" style={{ opacity: 0.6, cursor: 'default' }}>
                        <span>No se encontraron artículos con stock disponible</span>
                      </div>
                    </div>
                  )}
                </div>
                {articuloSeleccionado && (
                  <div className="proveedor-seleccionado">
                    ✓ Seleccionado: <strong>{articuloSeleccionado.articulo_nombre}</strong>
                    <span className="stock-info">
                      &nbsp;| Stock disponible: <strong>{articuloSeleccionado.stock_actual} {articuloSeleccionado.unidad_medida}</strong>
                    </span>
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Unidad de Medida</label>
                  <input
                    type="text"
                    value={formData.unidad_medida}
                    disabled
                    className="input-disabled"
                  />
                </div>

                <div className="form-group">
                  <label>Cantidad * {articuloSeleccionado && <span className="max-hint">(máx: {articuloSeleccionado.stock_actual})</span>}</label>
                  <input
                    type="number"
                    name="cantidad"
                    value={formData.cantidad}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0.01"
                    max={articuloSeleccionado ? articuloSeleccionado.stock_actual : undefined}
                    placeholder={articuloSeleccionado ? `Máximo: ${articuloSeleccionado.stock_actual}` : "Seleccione un artículo primero"}
                    required
                    disabled={!articuloSeleccionado}
                  />
                </div>
              </div>

              {/* Búsqueda de Proyecto */}
              <div className="form-group">
                <label>Proyecto (Destino) *</label>
                <div className="busqueda-proveedor">
                  <input
                    type="text"
                    value={busquedaProyecto}
                    onChange={(e) => {
                      setBusquedaProyecto(e.target.value);
                      setMostrarListaProyectos(true);
                      setProyectoSeleccionado(null);
                    }}
                    onFocus={() => setMostrarListaProyectos(true)}
                    placeholder="Buscar proyecto por nombre, cliente o apartamento..."
                    required
                  />
                  {mostrarListaProyectos && proyectosFiltrados.length > 0 && (
                    <div className="lista-proveedores">
                      {proyectosFiltrados.slice(0, 8).map(proy => (
                        <div
                          key={proy.id}
                          className="proveedor-item"
                          onClick={() => handleSeleccionarProyecto(proy)}
                        >
                          <strong>{proy.proyecto_nombre}</strong>
                          <span className="nit">{proy.nombre_cliente} - Apto: {proy.apartamento || 'N/A'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {mostrarListaProyectos && proyectosFiltrados.length === 0 && busquedaProyecto && (
                    <div className="lista-proveedores">
                      <div className="proveedor-item" style={{ opacity: 0.6, cursor: 'default' }}>
                        <span>No se encontraron proyectos</span>
                      </div>
                    </div>
                  )}
                </div>
                {proyectoSeleccionado && (
                  <div className="proveedor-seleccionado">
                    ✓ Seleccionado: <strong>{proyectoSeleccionado.proyecto_nombre}{proyectoSeleccionado.nombre_cliente ? ` - ${proyectoSeleccionado.nombre_cliente}` : ''}</strong>
                  </div>
                )}
              </div>

              {/* Capítulo del proyecto al que se carga la salida */}
              {proyectoSeleccionado && (
                <div className="form-group">
                  <label>Capítulo del proyecto {capitulos.length > 0 ? '*' : ''}</label>
                  {cargandoCapitulos ? (
                    <input type="text" value="Cargando capítulos..." readOnly disabled />
                  ) : capitulos.length > 0 ? (
                    <select value={capituloId} onChange={(e) => setCapituloId(e.target.value)} required>
                      <option value="">Seleccione el capítulo...</option>
                      {capitulos.map(c => (
                        <option key={c.id} value={c.id}>{c.numero ? `${c.numero}. ` : ''}{c.capitulo_nombre}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value="Este proyecto aún no tiene capítulos" readOnly disabled />
                  )}
                  <small style={{ color: '#64748b', marginTop: 4, display: 'block' }}>
                    {capitulos.length > 0
                      ? 'El valor de la salida suma en lo gastado de este capítulo.'
                      : 'La salida se registra al proyecto, sin capítulo.'}
                  </small>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Precio Unitario (auto)</label>
                  <input
                    type="text"
                    value={noCargarProyecto ? '$ 0' : (formData.precio_unitario ? formatMoney(parseFloat(formData.precio_unitario)) : '-')}
                    disabled
                    className="input-disabled"
                  />
                </div>

                <div className="form-group">
                  <label>Valor Total (auto)</label>
                  <input
                    type="text"
                    value={formatMoney((parseFloat(formData.cantidad) || 0) * (noCargarProyecto ? 0 : (parseFloat(formData.precio_unitario) || 0)))}
                    disabled
                    className="input-disabled"
                  />
                </div>

                <div className="form-group">
                  <label>Fecha de Salida *</label>
                  <input
                    type="date"
                    name="fecha_movimiento"
                    value={formData.fecha_movimiento}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="salida-precio0-check">
                  <input
                    type="checkbox"
                    checked={noCargarProyecto}
                    onChange={(e) => setNoCargarProyecto(e.target.checked)}
                  />
                  <span>
                    <strong>Precio $0 — no cargar al presupuesto del proyecto</strong>
                    <small>Marca esta opción si el material ya fue pagado en otra factura (y ya sumó al proyecto). La salida se registra igual y descuenta el stock, pero con valor $0 para no volver a sumar al gasto del proyecto.</small>
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label>Observaciones</label>
                <textarea
                  name="observaciones"
                  value={formData.observaciones}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Observaciones adicionales..."
                ></textarea>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancelar"
                  onClick={() => {
                    setMostrarForm(false);
                    resetForm();
                  }}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-guardar">
                  {modoEdicion ? 'Guardar Cambios' : 'Registrar Salida'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Confirmar Eliminación */}
      {mostrarConfirmDelete && salidaEliminar && (
        <div className="modal-overlay" onClick={() => setMostrarConfirmDelete(false)}>
          <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header warning">
              <h3>⚠️ Confirmar Eliminación</h3>
              <button className="btn-cerrar" onClick={() => setMostrarConfirmDelete(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="confirm-message">
                <span className="warning-icon">🗑️</span>
                <p>¿Está seguro que desea eliminar esta salida?</p>
                <p><strong>{salidaEliminar.articulo_nombre}</strong></p>
                <p>Cantidad: {salidaEliminar.cantidad} {salidaEliminar.unidad_medida}</p>
                <p>Proyecto: {salidaEliminar.proyecto_nombre || '-'}</p>
                <p className="warning-text">Esta acción no se puede deshacer y afectará el inventario.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancelar" onClick={() => setMostrarConfirmDelete(false)}>
                Cancelar
              </button>
              <button className="btn-eliminar" onClick={handleEliminar}>
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contador de resultados */}
      {searchTerm && (
        <div className="resultados-busqueda">
          Se encontraron <strong>{salidasFiltradas.length}</strong> resultado(s) para "{searchTerm}"
        </div>
      )}

      {/* Historial de Salidas */}
      {salidas.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📤</span>
          <h3>No hay salidas registradas</h3>
          <p>Registra la primera salida de artículos de la bodega</p>
          <button className="btn-primera" onClick={() => { resetForm(); setMostrarForm(true); }}>
            Registrar Primera Salida
          </button>
        </div>
      ) : salidasFiltradas.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">🔍</span>
          <h3>Sin resultados</h3>
          <p>No se encontraron salidas que coincidan con "{searchTerm}"</p>
          <button className="btn-primera" onClick={() => setSearchTerm('')}>
            Limpiar Búsqueda
          </button>
        </div>
      ) : (
        <div className="tabla-wrapper">
          <table className="salidas-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Artículo</th>
                <th>Cantidad</th>
                <th>Proyecto</th>
                <th>Capítulo</th>
                <th>Precio Unit.</th>
                <th>Valor Total</th>
                <th>Registrado por</th>
                <th>Observaciones</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {salidasFiltradas.map(salida => (
                <tr key={salida.id}>
                  <td className="fecha">{formatDate(salida.fecha_movimiento)}</td>
                  <td className="articulo">{salida.articulo_nombre}</td>
                  <td className="cantidad">
                    <span className="cantidad-badge salida">
                      -{salida.cantidad} {salida.unidad_medida}
                    </span>
                  </td>
                  <td className="proveedor">
                    <span className="proveedor-badge">
                      🏗️ {salida.proyecto_nombre || salida.proveedor_nombre || '-'}
                    </span>
                  </td>
                  <td className="capitulo">{salida.capitulo_nombre || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td className="precio">{formatMoney(salida.precio_unitario)}</td>
                  <td className="valor-total">{formatMoney(salida.valor_total)}</td>
                  <td className="col-usuario">
                    {salida.usuario_registro ? (
                      <>
                        <span className="usuario-nombre">{salida.usuario_registro}</span>
                        <span className="usuario-fecha">{formatDate(salida.fecha_movimiento)}</span>
                      </>
                    ) : <span className="usuario-vacio">-</span>}
                  </td>
                  <td className="observaciones">{salida.observaciones || '-'}</td>
                  <td className="acciones">
                    <button
                      className="btn-accion editar"
                      onClick={() => abrirModalEditar(salida)}
                      title="Editar salida"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-accion eliminar"
                      onClick={() => confirmarEliminar(salida)}
                      title="Eliminar salida"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SalidasBodega;
